import React from 'react'
import {
  Brain,
  Check,
  ArrowUp,
  ArrowDown,
  Eye,
  GitBranch,
  Cpu,
  BarChart3,
  ArrowUpDown,
} from 'lucide-react'
import { Project } from '../../../../types/models/project'
import { Model, ModelTreeNode } from '../../../../types/models/model'

const headerCommon = { background: 'linear-gradient(135deg, #333333, #404040)' }

export interface ModelCatalogCardProps {
  node: ModelTreeNode
  type: 'parent' | 'branch' | 'leaf'
  isSelected: boolean
  isActive?: boolean
  onClick: () => void
  onDetailClick?: (e: React.MouseEvent) => void
  onBranchClick?: (e: React.MouseEvent) => void
  project?: Project
  viewMode: 'grid' | 'list'
  parentModel?: Model
  comparison?: { improved: boolean | null }
  metricsDisplay?: Array<{ label: string; value: string; icon: any }>
}

const ModelCard: React.FC<ModelCatalogCardProps> = ({
  node,
  isSelected,
  isActive = false,
  onClick,
  onDetailClick,
  onBranchClick,
  project,
  viewMode,
  comparison,
  metricsDisplay,
}) => {
  const model = node.model

  const getModelStatus = (m: any) => {
    if (m.state === 'TRAINING') return 'training'
    if (m.state === 'TRAINED') return 'trained'
    if (m.state === 'ERR') return 'error'
    return 'unknown'
  }

  const getDeploymentStatus = (m: any) => m.deployment?.status || 'READY'
  const getDeploymentStatusText = (status: string) => {
    switch (status) {
      case 'IN_SERVICE':
        return 'Deployed'
      case 'CREATING':
        return 'Deploying'
      case 'ERR':
        return 'Deploy Failed'
      case 'UPDATING':
        return 'Updating'
      case 'DELETING':
        return 'Deleting'
      case 'READY':
        return 'Ready'
      default:
        return 'Ready'
    }
  }

  return (
    <div
      onClick={onClick}
      className="bg-dark border position-relative"
      style={{
        borderWidth: 2,
        borderColor: isActive ? '#10b981' : isSelected ? '#667eea' : '#404040',
        transition: 'all .3s ease',
        cursor: 'pointer',
        overflow: 'hidden',
        borderRadius: viewMode === 'grid' ? 16 : 8,
        marginBottom: viewMode === 'list' ? '.5rem' : undefined,
        height: viewMode === 'grid' ? '100%' : undefined,
        boxShadow: isSelected
          ? '0 0 0 1px rgba(102,126,234,.3)'
          : isActive
          ? '0 0 0 1px rgba(16,185,129,.3)'
          : undefined,
        borderLeft: isSelected ? '6px solid #667eea' : isActive ? '6px solid #10b981' : undefined,
      }}
      onMouseEnter={(e) => {
        const el = e.currentTarget as HTMLDivElement
        el.style.boxShadow = '0 12px 24px rgba(0,0,0,.4)'
        if (viewMode === 'list') {
          el.style.background = '#333333'
        }
        el.style.borderColor = '#667eea'
      }}
      onMouseLeave={(e) => {
        const el = e.currentTarget as HTMLDivElement
        el.style.boxShadow = isSelected
          ? '0 0 0 1px rgba(102,126,234,.3)'
          : isActive
          ? '0 0 0 1px rgba(16,185,129,.3)'
          : ''
        if (viewMode === 'list') {
          el.style.background = ''
        }
        el.style.borderColor = isActive ? '#10b981' : isSelected ? '#667eea' : '#404040'
      }}
    >
      {isSelected && (
        <div
          className="selected-indicator bg-gradient-primary text-white d-flex align-items-center justify-content-center"
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            width: 32,
            height: 32,
            borderRadius: '50%',
            boxShadow: '0 4px 8px rgba(0,0,0,.4)',
            zIndex: 3,
          }}
        >
          <Check size={18} />
        </div>
      )}

      <button
        className="detail-button bg-gradient-success text-white d-flex align-items-center justify-content-center border-0"
        onClick={onDetailClick}
        style={{
          position: 'absolute',
          top: 12,
          right: 56,
          width: 32,
          height: 32,
          borderRadius: '50%',
          boxShadow: '0 4px 8px rgba(0,0,0,.4)',
          zIndex: 3,
          cursor: 'pointer',
          transition: 'all .2s',
        }}
      >
        <Eye size={14} />
      </button>

      {comparison && comparison.improved !== null && viewMode === 'grid' && (
        <div
          className={`position-absolute top-0 start-0 mt-2 ms-2 rounded-circle d-flex align-items-center justify-content-center ${
            comparison.improved === true ? 'bg-gradient-success' : 'bg-gradient-danger'
          }`}
          style={{
            width: 32,
            height: 32,
            boxShadow: '0 4px 8px rgba(0,0,0,.4)',
            display: comparison.improved !== null ? 'flex' : 'none',
          }}
        >
          {comparison.improved ? <ArrowUp size={16} /> : <ArrowDown size={16} />}
        </div>
      )}

      {node.children && node.children.length > 0 && onBranchClick && viewMode === 'grid' && (
        <div
          onClick={onBranchClick}
          className="position-absolute top-0 start-0 mt-2 ms-5 rounded-circle bg-gradient-purple text-white d-flex align-items-center justify-content-center"
          style={{
            width: 32,
            height: 32,
            boxShadow: '0 4px 8px rgba(0,0,0,.4)',
            cursor: 'pointer',
          }}
        >
          <GitBranch size={16} />
        </div>
      )}

      <div
        style={{
          ...headerCommon,
          position: 'relative',
          padding: viewMode === 'grid' ? '4rem 1.5rem 1rem' : '1rem 1.5rem',
          borderBottom: viewMode === 'grid' ? '1px solid #505050' : undefined,
        }}
        className={viewMode === 'grid' ? '' : 'd-flex align-items-center gap-3'}
      >
        {viewMode === 'list' ? (
          <>
            <div
              className="model-icon bg-gradient-primary d-flex align-items-center justify-content-center"
              style={{
                width: 40,
                height: 40,
                borderRadius: 8,
                boxShadow: '0 2px 4px rgba(0,0,0,.3)',
              }}
            >
              <Brain size={20} />
            </div>
            <div className="model-info d-flex align-items-center gap-3 flex-grow-1 min-w-0">
              <div className="model-name-section" style={{ width: 250, flexShrink: 0 }}>
                <h5 className="model-name fw-bold text-white mb-1 text-truncate">{model.name}</h5>
                <div className="model-id text-secondary small text-truncate">ID: {model.id}</div>
              </div>

              {metricsDisplay && (
                <div className="model-metrics d-flex gap-3" style={{ width: 400, flexShrink: 0 }}>
                  {metricsDisplay.map((metric, index) => (
                    <div
                      key={index}
                      className="metric-item text-center"
                      style={{ width: 85, flexShrink: 0 }}
                    >
                      <div className="metric-value fw-bold text-white">{metric.value}</div>
                      <div className="metric-label text-secondary small">{metric.label}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="model-badges">
              <span
                className={'badge text-white text-uppercase'}
                style={{
                  background:
                    getModelStatus(model) === 'trained'
                      ? 'linear-gradient(135deg,#10b981,#059669)'
                      : getModelStatus(model) === 'training'
                      ? 'linear-gradient(135deg,#f59e0b,#d97706)'
                      : getModelStatus(model) === 'error'
                      ? 'linear-gradient(135deg,#ef4444,#dc2626)'
                      : 'linear-gradient(135deg,#6b7280,#4b5563)',
                  padding: '.4rem .8rem',
                  borderRadius: 20,
                  width: 90,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 4px rgba(0,0,0,.3)',
                  letterSpacing: '.5px',
                  fontSize: '.8rem',
                }}
              >
                {getModelStatus(model)}
              </span>
              <span
                className={'badge text-white text-uppercase'}
                style={{
                  background: (() => {
                    const s = getDeploymentStatus(model)
                    switch (s) {
                      case 'IN_SERVICE':
                        return 'linear-gradient(135deg,#10b981,#059669)'
                      case 'CREATING':
                        return 'linear-gradient(135deg,#f59e0b,#d97706)'
                      case 'ERR':
                        return 'linear-gradient(135deg,#ef4444,#dc2626)'
                      case 'UPDATING':
                        return 'linear-gradient(135deg,#3b82f6,#1d4ed8)'
                      case 'DELETING':
                        return 'linear-gradient(135deg,#f59e0b,#d97706)'
                      case 'READY':
                        return 'linear-gradient(135deg,#667eea,#764ba2)'
                      default:
                        return 'linear-gradient(135deg,#667eea,#764ba2)'
                    }
                  })(),
                  padding: '.4rem .8rem',
                  borderRadius: 20,
                  width: 90,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 4px rgba(0,0,0,.3)',
                  letterSpacing: '.5px',
                  fontSize: '.8rem',
                }}
              >
                {getDeploymentStatusText(getDeploymentStatus(model))}
              </span>
            </div>
          </>
        ) : (
          <>
            <div
              className="model-icon bg-gradient-primary d-flex align-items-center justify-content-center"
              style={{
                position: 'absolute',
                top: 16,
                left: 16,
                width: 48,
                height: 48,
                borderRadius: 12,
                color: 'white',
                boxShadow: '0 4px 8px rgba(0,0,0,.4)',
              }}
            >
              <Brain size={24} />
            </div>
            <h5
              className="model-name fw-bold text-white mb-2"
              style={{
                marginLeft: '3.5rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {model.name}
            </h5>
            <div
              className="model-id text-secondary"
              style={{ marginLeft: '3.5rem', fontFamily: 'Monaco, Menlo, monospace' }}
            >
              ID: {model.id}
            </div>
            <div
              className="model-badges d-flex gap-2 align-items-center position-absolute"
              style={{ top: '1rem', right: '8rem' }}
            >
              <span
                className={'badge text-white text-uppercase'}
                style={{
                  background:
                    getModelStatus(model) === 'trained'
                      ? 'linear-gradient(135deg,#10b981,#059669)'
                      : getModelStatus(model) === 'training'
                      ? 'linear-gradient(135deg,#f59e0b,#d97706)'
                      : getModelStatus(model) === 'error'
                      ? 'linear-gradient(135deg,#ef4444,#dc2626)'
                      : 'linear-gradient(135deg,#6b7280,#4b5563)',
                  padding: '.4rem .8rem',
                  borderRadius: 20,
                  width: 90,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 4px rgba(0,0,0,.3)',
                  letterSpacing: '.5px',
                  fontSize: '.8rem',
                }}
              >
                {getModelStatus(model)}
              </span>
              <span
                className={'badge text-white text-uppercase'}
                style={{
                  background: (() => {
                    const s = getDeploymentStatus(model)
                    switch (s) {
                      case 'IN_SERVICE':
                        return 'linear-gradient(135deg,#10b981,#059669)'
                      case 'CREATING':
                        return 'linear-gradient(135deg,#f59e0b,#d97706)'
                      case 'ERR':
                        return 'linear-gradient(135deg,#ef4444,#dc2626)'
                      case 'UPDATING':
                        return 'linear-gradient(135deg,#3b82f6,#1d4ed8)'
                      case 'DELETING':
                        return 'linear-gradient(135deg,#f59e0b,#d97706)'
                      case 'READY':
                        return 'linear-gradient(135deg,#667eea,#764ba2)'
                      default:
                        return 'linear-gradient(135deg,#667eea,#764ba2)'
                    }
                  })(),
                  padding: '.4rem .8rem',
                  borderRadius: 20,
                  width: 90,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 4px rgba(0,0,0,.3)',
                  letterSpacing: '.5px',
                  fontSize: '.8rem',
                }}
              >
                {getDeploymentStatusText(getDeploymentStatus(model))}
              </span>
            </div>
          </>
        )}
      </div>

      {viewMode === 'grid' && metricsDisplay && (
        <div
          className="d-grid"
          style={{ gridTemplateColumns: '1fr 1fr', gap: '1rem', padding: '1.5rem' }}
        >
          {metricsDisplay.slice(0, 4).map((metric, index) => (
            <div key={index} className="d-flex align-items-center gap-2">
              <div
                className="rounded-3 d-flex align-items-center justify-content-center"
                style={{
                  width: 40,
                  height: 40,
                  background: 'linear-gradient(135deg, rgba(102,126,234,.3), rgba(118,74,162,.3))',
                  color: '#667eea',
                }}
              >
                <metric.icon size={20} />
              </div>
              <div>
                <div className="text-secondary small fw-medium mb-1">{metric.label}</div>
                <div className="fw-bold text-white">{metric.value}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default ModelCard
